/**
 * Zavírání rozbalených nabídek (profil, RowMenu, flyout menu) — jedno místo pro všechny.
 *
 * Proč `pointerdown` a ne `onBlur` + `setTimeout`: na iPadu vzniká z ťuknutí simulovaná dvojice
 * mousedown/click; `preventDefault()` na mousedown (obvyklý trik proti předčasnému blur) způsobí,
 * že Safari už `click` vůbec nepošle a výběr z nabídky na dotyku nefunguje. Safari navíc tlačítku
 * při ťuknutí nedává fokus, takže `relatedTarget` v blur je null. Zavírá se proto podle toho,
 * KDE se ťuklo — `pointerdown` pokrývá myš, dotyk i pero a přijde dřív než `click`.
 */

import { useEffect, useRef } from 'react'

/**
 * Dokud je `open`, hlídá ťuknutí mimo vrácený prvek (a mimo `extraRefs`, např. portál s nabídkou)
 * a klávesu Escape; obojí volá `onClose`. Vrácený ref patří na obal tlačítka i nabídky.
 */
export function useDismissOnOutside<T extends HTMLElement>(
  open: boolean,
  onClose: () => void,
  extraRefs: React.RefObject<HTMLElement>[] = [],
) {
  const ref = useRef<T>(null)
  // Callback i extra refy přes ref: jinak by se posluchač přihlašoval při každém překreslení.
  const close = useRef(onClose)
  close.current = onClose
  const extras = useRef(extraRefs)
  extras.current = extraRefs

  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node
      if (ref.current?.contains(target)) return
      if (extras.current.some((r) => r.current?.contains(target))) return
      close.current()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close.current()
    }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return ref
}
